import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Modal from '../components/Modal';

interface ITraining {
  id: string;
  code: string;
  name: string;
  start_date: string;
  end_date: string;
  trainer_name: string;
  location: string;
}

interface PendingApproval {
  approvalInstanceId: string;
  training_name: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  currentLevel: number;
  finalLevel: number;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search
    ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase()))
    : employees;
  return filtered
    .slice(0, 20)
    .map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function Training() {
  const [trainings, setTrainings] = useState<ITraining[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [assignTarget, setAssignTarget] = useState<ITraining | null>(null);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignType, setAssignType] = useState('Direct');
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [myAssignments, setMyAssignments] = useState<any[]>([]);
  const [attendanceTarget, setAttendanceTarget] = useState<any>(null);
  const [attendanceForm, setAttendanceForm] = useState({ dateAttended: '', certificateObtained: false, certificateNumber: '', certificateExpiryDate: '', trainerFeedback: '' });
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    trainerName: '',
    location: '',
    startDate: '',
    endDate: '',
    durationHours: '',
    cost: '',
    availableToAll: false,
  });

  useEffect(() => {
    fetchTrainings();
    fetchPendingApprovals();
    fetchMyAssignments();
  }, []);

  const fetchMyAssignments = async () => {
    try {
      const res = await api.get('/hr/training/my-assignments');
      setMyAssignments(res.data.assignments || []);
    } catch (error) {
      console.error('Failed to fetch my training assignments:', error);
    }
  };

  const submitAttendance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!attendanceTarget) return;
    try {
      await api.put(`/hr/training/assignments/${attendanceTarget.id}/attendance`, attendanceForm);
      setAttendanceTarget(null);
      fetchMyAssignments();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to record attendance');
    }
  };

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/hr/training');
      setTrainings(response.data.trainings || []);
    } catch (error) {
      console.error('Failed to fetch trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await api.get('/hr/training/my-approvals');
      setPendingApprovals(res.data.approvals || []);
    } catch (error) {
      console.error('Failed to fetch pending training approvals:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/hr/training', formData);
      setFormData({
        code: '',
        name: '',
        description: '',
        trainerName: '',
        location: '',
        startDate: '',
        endDate: '',
        durationHours: '',
        cost: '',
        availableToAll: false,
      });
      setShowForm(false);
      fetchTrainings();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create training');
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignTarget || !assignEmployeeId) return;
    try {
      await api.post(`/hr/training/${assignTarget.id}/assign`, {
        employeeIds: [assignEmployeeId],
        assignmentType: assignType,
      });
      setAssignTarget(null);
      setAssignEmployeeId('');
      setAssignType('Direct');
      alert('Training assigned successfully');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to assign training');
    }
  };

  const handleApprovalAction = async (approvalInstanceId: string, action: 'APPROVED' | 'REJECTED') => {
    try {
      await api.put(`/hr/training/approvals/${approvalInstanceId}`, { action });
      fetchPendingApprovals();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to act on approval');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Training Management</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Create Training'}</Button>
      </div>

      {pendingApprovals.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-amber-900 mb-3">
            Pending Training Requests Awaiting Your Approval ({pendingApprovals.length})
          </h2>
          <ul className="space-y-2">
            {pendingApprovals.map((approval) => (
              <li
                key={approval.approvalInstanceId}
                className="flex justify-between items-center bg-white rounded-lg p-3 text-sm"
              >
                <span>
                  {approval.first_name} {approval.last_name} ({approval.employee_code}) — requesting{' '}
                  <strong>{approval.training_name}</strong> (level {approval.currentLevel}/
                  {approval.finalLevel})
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprovalAction(approval.approvalInstanceId, 'APPROVED')}
                    className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleApprovalAction(approval.approvalInstanceId, 'REJECTED')}
                    className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Training Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
              <Input
                placeholder="Training Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <Input
                placeholder="Trainer Name"
                value={formData.trainerName}
                onChange={(e) => setFormData({ ...formData, trainerName: e.target.value })}
              />
              <Input
                placeholder="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
              <Input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
              <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="px-4 py-2 border rounded-lg col-span-2"
                rows={3}
              />
            </div>
            <Button type="submit" className="w-full">
              Create Training
            </Button>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-600">Loading trainings...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trainings.map((training) => (
            <div key={training.id} className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-lg mb-2">{training.name}</h3>
              <p className="text-sm text-gray-600 mb-2">Code: {training.code}</p>
              <p className="text-sm text-gray-600 mb-2">Trainer: {training.trainer_name}</p>
              <p className="text-sm text-gray-600 mb-4">Location: {training.location}</p>
              <button
                onClick={() => setAssignTarget(training)}
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                Assign to Employee
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-3">My Training Assignments</h2>
        <div className="bg-white rounded-lg shadow divide-y">
          {myAssignments.length === 0 && <p className="p-4 text-sm text-gray-500">No training assigned to you yet</p>}
          {myAssignments.map((a) => (
            <div key={a.id} className="p-4 flex justify-between items-center text-sm">
              <div>
                <p className="font-medium">{a.training_name}</p>
                <p className="text-gray-500">
                  {a.assignment_type} — {a.attendance_status || 'Pending'}
                  {a.certificate_obtained ? ' — Certified' : ''}
                </p>
              </div>
              <button
                className="text-blue-600 hover:underline"
                onClick={() => {
                  setAttendanceTarget(a);
                  setAttendanceForm({
                    dateAttended: a.date_attended || '',
                    certificateObtained: a.certificate_obtained || false,
                    certificateNumber: a.certificate_number || '',
                    certificateExpiryDate: a.certificate_expiry_date || '',
                    trainerFeedback: a.trainer_feedback || '',
                  });
                }}
              >
                Submit Attendance
              </button>
            </div>
          ))}
        </div>
      </div>

      <Modal isOpen={!!attendanceTarget} onClose={() => setAttendanceTarget(null)} title={`Attendance — ${attendanceTarget?.training_name}`}>
        <form onSubmit={submitAttendance} className="space-y-4">
          <Input
            label="Date Attended"
            type="date"
            value={attendanceForm.dateAttended}
            onChange={(e) => setAttendanceForm({ ...attendanceForm, dateAttended: e.target.value })}
            required
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={attendanceForm.certificateObtained}
              onChange={(e) => setAttendanceForm({ ...attendanceForm, certificateObtained: e.target.checked })}
            />
            Certificate obtained
          </label>
          {attendanceForm.certificateObtained && (
            <>
              <Input
                label="Certificate No."
                value={attendanceForm.certificateNumber}
                onChange={(e) => setAttendanceForm({ ...attendanceForm, certificateNumber: e.target.value })}
              />
              <Input
                label="Certificate Expiry"
                type="date"
                value={attendanceForm.certificateExpiryDate}
                onChange={(e) => setAttendanceForm({ ...attendanceForm, certificateExpiryDate: e.target.value })}
              />
            </>
          )}
          <textarea
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Trainer feedback"
            value={attendanceForm.trainerFeedback}
            onChange={(e) => setAttendanceForm({ ...attendanceForm, trainerFeedback: e.target.value })}
          />
          <Button type="submit" className="w-full">
            Save
          </Button>
        </form>
      </Modal>

      <Modal
        isOpen={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        title={`Assign "${assignTarget?.name}"`}
      >
        <form onSubmit={handleAssign} className="space-y-4">
          <LookupSelect
            label="Employee"
            value={assignEmployeeId}
            onChange={(value) => setAssignEmployeeId(value)}
            fetchOptions={fetchEmployeeOptions}
            placeholder="Search employee..."
            required
          />
          <Select
            label="Assignment Type"
            value={assignType}
            onChange={(e) => setAssignType(e.target.value)}
            options={[
              { value: 'Direct', label: 'Direct (HR-assigned, no approval needed)' },
              { value: 'Request', label: 'Request (employee-initiated, needs approval)' },
            ]}
          />
          <Button type="submit" className="w-full">
            Assign
          </Button>
        </form>
      </Modal>
    </div>
  );
}
